import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/ui/Icon';
import { Button } from '@/components/ui/Button';
import { CancelButton } from '@/components/ui/CancelButton';
import { ICONS } from '@/constants/icons';
import { cn } from '@/utils/cn';

interface ResetCalendarDialogProps {
  isOpen: boolean;
  onClose: () => void;
  calendarName: string;
  onConfirm: (target: 'all' | 'posts' | 'campaigns') => void;
  isLoading?: boolean;
}

type ResetTarget = 'all' | 'posts' | 'campaigns';

export const ResetCalendarDialog = ({
  isOpen,
  onClose,
  calendarName,
  onConfirm,
  isLoading = false,
}: ResetCalendarDialogProps) => {
  const { t } = useTranslation(['calendar', 'common']);
  const [selectedTarget, setSelectedTarget] = useState<ResetTarget>('all');

  const RESET_OPTIONS: Array<{ value: ResetTarget; label: string; description: string }> = [
    { value: 'all', label: t('calendar_reset_delete_all'), description: t('calendar_reset_delete_all_desc') },
    { value: 'posts', label: t('calendar_reset_only_posts'), description: t('calendar_reset_only_posts_desc') },
    { value: 'campaigns', label: t('calendar_reset_only_campaigns'), description: t('calendar_reset_only_campaigns_desc') },
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={() => !isLoading && onClose()}
      />

      {/* Dialog */}
      <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-red-100 rounded-full">
            <Icon name={ICONS.WARNING} size="md" color="text-red-600" ariaHidden />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{t('calendar_reset_title')}</h2>
            <p className="text-sm text-gray-500">{calendarName}</p>
          </div>
        </div>

        {/* Warning */}
        <p className="text-sm text-gray-600 mb-4">
          {t('calendar_reset_warning')}
        </p>

        {/* Options */}
        <div className="space-y-2 mb-6">
          {RESET_OPTIONS.map((option) => (
            <label
              key={option.value}
              className={cn(
                'flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors',
                selectedTarget === option.value
                  ? 'border-red-500 bg-red-50'
                  : 'border-gray-200 hover:border-gray-300'
              )}
            >
              <input
                type="radio"
                name="resetTarget"
                value={option.value}
                checked={selectedTarget === option.value}
                onChange={() => setSelectedTarget(option.value)}
                className="mt-0.5"
                disabled={isLoading}
              />
              <div>
                <span className="font-medium text-gray-900">{option.label}</span>
                <p className="text-sm text-gray-500">{option.description}</p>
              </div>
            </label>
          ))}
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-1.5 sm:gap-2">
          <CancelButton onClick={onClose} disabled={isLoading} />
          <Button
            variant="primary"
            onClick={() => onConfirm(selectedTarget)}
            disabled={isLoading}
            className="bg-red-600 hover:bg-red-700"
          >
            {isLoading ? t('deleting') : t('calendar_reset_button')}
          </Button>
        </div>
      </div>
    </div>
  );
};
